import { useEffect, useRef } from 'react'
import { STEP, hashForStep, stepFromHash } from '../constants/steps'

/**
 * Mirrors the wizard step in the URL fragment and listens to the browser's
 * Back/Forward buttons.
 *
 *  - every step change pushes one history entry (`#/a`, `#/review`, ...);
 *  - Back/Forward to a step the user has already reached moves there through
 *    `onHistoryStep`; a fragment pointing past that (typed by hand, or a stale
 *    link) is rewritten to the current step, so the form cannot be skipped into;
 *  - once the PDF has been downloaded and the data wiped, history can no longer
 *    leave the finish screen.
 *
 * The fragment carries no data. Deep links are intentionally not supported: a
 * reload resumes from the step saved in sessionStorage.
 */
const useStepHistory = ({ step, onHistoryStep, locked = false }) => {
	const furthestStep = useRef(step)
	const lockedRef = useRef(locked)
	const stepRef = useRef(step)
	const onHistoryStepRef = useRef(onHistoryStep)
	const isFirstRun = useRef(true)
	lockedRef.current = locked
	stepRef.current = step
	onHistoryStepRef.current = onHistoryStep

	useEffect(() => {
		furthestStep.current = Math.max(furthestStep.current, step)
		const wanted = hashForStep(step)
		if (window.location.hash === wanted) {
			isFirstRun.current = false
			return
		}
		// the first entry replaces whatever fragment the page was opened with
		const method = isFirstRun.current ? 'replaceState' : 'pushState'
		isFirstRun.current = false
		window.history[method](null, '', wanted)
	}, [step])

	useEffect(() => {
		const onPopState = () => {
			const target = stepFromHash(window.location.hash)
			const allowed =
				target !== null &&
				target <= furthestStep.current &&
				!(lockedRef.current && target !== STEP.FINISH)
			if (!allowed) {
				window.history.replaceState(null, '', hashForStep(stepRef.current))
				return
			}
			if (target !== stepRef.current) onHistoryStepRef.current(target)
		}
		window.addEventListener('popstate', onPopState)
		return () => window.removeEventListener('popstate', onPopState)
	}, [])
}

export default useStepHistory
